import {
  type Attachment as GQLAttachment,
  type EventArtistRider as GQLItem,
  EventArtistRiderTypeEnum,
} from '../../../generated/api.graphql';
import { type Attachment, gqlStatusMap } from '../../Attachment/Attachment';
import { take } from '../../../util';

export const EventArtistRiderType = Object.freeze({
  HOSPITALITY: EventArtistRiderTypeEnum.Hospitality,
  TECH: EventArtistRiderTypeEnum.Tech,
});

export type EventArtistRiderType = (typeof EventArtistRiderType)[keyof typeof EventArtistRiderType];

export type EventArtistRider = {
  id?: number;
  notes?: string;
  riderType?: EventArtistRiderType;
  // Nullable: `null` clears the field on save (the store's `take` keeps `null` but drops
  // `undefined`), matching the schema's nullable columns.
  artistLoadInTime?: string | null;
  stageSoundCheckTime?: string | null;
  soundCheckType?: string | null;
  stagePerformersNumber?: number | null;
  backstagePassesNumber?: number | null;
  attachments?: Attachment[];
};

/**
 * The shared `Attachment.fromGql` is bound to the *legacy* generated type, so AppSchema needs its
 * own mapper. Only the mapper is local: the domain type and its status map are shared, because
 * both schemas emit the same `AttachmentStatusEnum` wire values and the rider screens
 * (`RiderDocumentsSection.vue`'s prop) already bind to that type.
 * Same call as `Internal/Whitelabel`; see doc/claude/api-layer.md.
 */
const attachmentFromGql = (item: GQLAttachment): Attachment => ({
  // No `size`: the fragment deliberately does not select it — see the comment on
  // `EVENT_ARTIST_RIDER_FIELDS`. Taking a key the selection omits would only ever copy
  // `undefined`, so leaving it in would read as though the field were still available.
  ...take<Attachment>(item, 'id', 'name', 'mime', 'checksum'),
  // `url` is nullable on the schema and optional here, and `take` passes `null` straight
  // through — it only skips `undefined`.
  url: item.url ?? undefined,
  status: item.status ? gqlStatusMap[item.status] : undefined,
});

export const fromGql = (item: GQLItem): EventArtistRider => ({
  ...take<EventArtistRider>(item, 'id', 'notes', 'artistLoadInTime', 'stageSoundCheckTime', 'soundCheckType', 'stagePerformersNumber', 'backstagePassesNumber'),
  riderType: item.riderType as EventArtistRiderType,
  attachments: item.attachments?.map(attachmentFromGql) || [],
});
